import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import DocumentSettings from "../../../document/DocumentSettings";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	Security,
	SelectionAnnotation,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxAnnotationAnnotation
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import DeleteAnnotationDialogFields from "../../field/annotation/DeleteAnnotationDialogFields";
import {InputNumber} from "primereact/inputnumber";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface DeleteAnnotationDialogStatus extends PortalDialogState<ToolboxAnnotationAnnotation> {
	pages: number;
}

export class DeleteAnnotationDialogDefinition extends PortalDialog<ToolboxAnnotationAnnotation,
	PortalDialogProperties<ToolboxAnnotationAnnotation>, DeleteAnnotationDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			fieldErrors: new Map<DeleteAnnotationDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.editAnnotation, Functions.getEditAnnotationDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.ANNOTATION_DELETE, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter?.remove?.selection?.select)) {
			return null;
		}

		let removeParameter: SelectionAnnotation = parameter!.remove!.selection!.select![0];

		return (
			<>
				<fieldset className="p-col-12 p-grid" hidden={
					!this.state.fields?.contains(DeleteAnnotationDialogFields.PAGE)
				}>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-page">
							<Translate {...this.props} data="delete_annotation.page" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputNumber inputId="control-page" showButtons={true} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 removeParameter.page, SelectionAnnotation.getPageDefault()
										 )}
										 min={SelectionAnnotation.getPageMin()} step={1} max={this.state.pages}
										 onChange={(e) => this.onChange(
											 DeleteAnnotationDialogFields.PAGE, e.value
										 )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["delete-annotation-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					resizable={false} appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.delete_annotation.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: DeleteAnnotationDialogFields, value: any): void {
		let parameter: ToolboxAnnotationAnnotation = this.state.parameter!.data;
		let removeParameter: SelectionAnnotation = parameter.remove!.selection!.select![0];

		switch (field) {
			case DeleteAnnotationDialogFields.PAGE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = SelectionAnnotation.getPageDefault();
				}

				removeParameter.page = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-delete small"}/>
					<Translate {...this.props} data="delete_annotation.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModifyAnnotations, Security.getCanModifyAnnotationsDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: ToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];

		if (!FunctionHelper.exists(parameter) || !FunctionHelper.exists(portalDocument)) {
			return;
		}

		let removeParameter: SelectionAnnotation = parameter!.remove!.selection!.select![0];
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let currentPage: string = documentSettings.activeViewer.currentPage.toString();
		let pageCount: number = await portalDocument.getNumberOfPages();

		removeParameter.page = parseInt(currentPage);

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			pages: pageCount
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let parameter: ToolboxAnnotationAnnotation = this.state.parameter!.data!;
		let removeParameter: SelectionAnnotation = parameter.remove!.selection!.select![0];
		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			annotation: parameter
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.annotation.page", MessageType.SUCCESS);
			message.context = {page: removeParameter.page};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.ANNOTATION_DELETE;
	}

	protected getDefaultParameter(): Parameter<ToolboxAnnotationAnnotation> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const DeleteAnnotationDialog = React.memo(DeleteAnnotationDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxAnnotationAnnotation>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxAnnotationAnnotation>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default DeleteAnnotationDialog;